// Global Instructions Rule Applied!
//
// Investor statement settings (spec A3.14 stage 5): what is about the
// STATEMENTS of a fund or a consolidated fund — the layout, the partners'
// capital page, the entity statements of a consolidated fund and the wording
// of the two documents. (What a statement prints ABOUT the fund — legal
// name, address, signatory — are fund terms, in TermsTab's own sections.)
//
// One component, two homes: the funds Terms tab (`fundId`), and the
// Statements tab of a consolidated fund (`fundGroupId`), which has no Terms
// tab. Merge On Send (stage 6) is about what a statement run SENDS: one PDF
// per account and fund, or the stored PDFs as they are. Include Fund Report
// (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1) is about what a run
// GENERATES: the Quarterly Fund Report as a third document, beside the
// statements. The capital call notice sections are the pattern
// (NoticeSettingsSections.tsx): fields auto-PATCH one at a time to the
// settings route of the entity; the settings row is created on first save.
//
// A wording field shows the text that PRINTS: the entity's own wording where
// it has one, else the default. Editing it stores an override; clearing it
// (or typing the default back) returns to the default.

import React, { useMemo } from 'react'
import { Alert, Col, Spin, Tag } from 'antd'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldConfig, FieldTypeEnum, SelectTypeEnum } from '../../../../../core/components/InputManager/types.ts'
import { API_ROUTES } from '../../../../../core/utils/endpoints.ts'
import { useFundStatementSettings } from '../../../hooks/useFundStatementSettings.ts'
import {
  FundStatementSettings,
  FundStatementSettingsRowFields
} from '../../../../../state_manager/data_models/fund_statement_settings.ts'

// InputManager builds `${backend}/${patchEndpoint}`: the route path without
// its leading slash.
const FUND_PATCH = API_ROUTES.FUNDS.STATEMENT_SETTINGS_PATCH.path.replace(/^\//, '')
const FUND_GROUP_PATCH = API_ROUTES.FUND_GROUPS.STATEMENT_SETTINGS_PATCH.path.replace(/^\//, '')

// A wording field's key: not a column (the blocks share one jsonb column),
// so its valueTransformer builds the PATCH body.
const WORDING_KEY_PREFIX = 'statementwording__'

interface StatementSettingsSectionProps {
  /** The fund whose settings these are ... */
  fundId?: string | null
  /** ... or the consolidated fund. Exactly one of the two. */
  fundGroupId?: string | null
  /** One label-column width across the tab (TermsTab's maxLabelWidth). */
  maxLabelWidth: number
}

export function getStatementSettingFields(
  settings: FundStatementSettings,
  patchEndpoint: string,
  isConsolidated: boolean
): FieldConfig[] {
  const row = settings.settings
  const fields: FieldConfig[] = []
  if (isConsolidated) {
    // A consolidated fund has no Terms tab, so the legal name its statements
    // print is entered here (a fund's is on its Terms tab, under Details).
    // The key's column part is what the PATCH carries: { legal_name }.
    fields.push({
      key: 'fund_groups__legal_name',
      label: 'Legal Name',
      type: FieldTypeEnum.TEXT,
      value: settings.fund_group?.fund_groups__legal_name ?? null,
      editable: true,
      patchEndpoint,
      labelTooltip:
        'The legal name of the consolidated fund, as its statements print it. A statement run does not generate a consolidated fund that has none.'
    })
  }
  fields.push(
    {
      key: FundStatementSettingsRowFields.fund_statement_settings__statement_layout_id,
      label: 'Layout',
      type: FieldTypeEnum.SELECT,
      // the layout that APPLIES, so an unset fund shows the default it gets
      value: settings.prog__layout_id ?? null,
      options: settings.layouts.map((l) => ({ value: l.id, label: l.is_default ? `${l.label} (default)` : l.label })),
      selectType: SelectTypeEnum.FILTER,
      allowClear: true,
      editable: true,
      patchEndpoint,
      labelTooltip: (
        <div>
          <div className='mb-1'>
            How the balance sheet, the statement of operations and the capital account are arranged, on screen and on
            the printed statements.
          </div>
          {settings.layouts.map((l) => (
            <div key={l.id} className='mb-1'>
              <strong>{l.label}</strong>
              {l.description ? ` — ${l.description}` : ''}
            </div>
          ))}
          <div>Clear the field to return to the default layout.</div>
        </div>
      )
    },
    {
      key: FundStatementSettingsRowFields.fund_statement_settings__show_partners_capital,
      label: "Partners' Capital Page",
      type: FieldTypeEnum.SWITCH,
      value: row.fund_statement_settings__show_partners_capital,
      editable: true,
      patchEndpoint,
      labelTooltip:
        "Adds the partners' capital page, with the allocation of unrealized gain, to the Fund Financial Statements. The page needs carried interest, so it prints nothing until the statements state it."
    },
    {
      key: FundStatementSettingsRowFields.fund_statement_settings__merge_on_send,
      label: 'Merge On Send',
      type: FieldTypeEnum.SWITCH,
      value: row.fund_statement_settings__merge_on_send,
      editable: true,
      patchEndpoint,
      labelTooltip: isConsolidated
        ? 'When a statement run is sent, each investor account receives one PDF for the consolidated fund: the fund report when it is in the send, its consolidated statement, its entity statements and the consolidated financial statements, in that order. Off: the stored PDFs are attached as they are.'
        : "When a statement run is sent, each investor account receives one PDF for this fund: the fund report when it is in the send, its capital account statement and the fund's financial statements, in that order. Off: the stored PDFs are attached as they are."
    },
    {
      key: FundStatementSettingsRowFields.fund_statement_settings__include_fund_report,
      label: 'Include Fund Report',
      type: FieldTypeEnum.SWITCH,
      value: row.fund_statement_settings__include_fund_report,
      editable: true,
      patchEndpoint,
      labelTooltip: isConsolidated
        ? 'A statement run also generates the Quarterly Fund Report of the consolidated fund, as its own document group: the reports are generated, marked as issued and sent on their own, so they can go to the investors before the statements. The report needs the Period Highlights of the period signed off.'
        : 'A statement run also generates the Quarterly Fund Report of this fund, as its own document group: the reports are generated, marked as issued and sent on their own, so they can go to the investors before the statements. The report needs the Period Highlights of the period signed off.'
    }
  )
  if (isConsolidated) {
    fields.push({
      key: FundStatementSettingsRowFields.fund_statement_settings__send_entity_statements,
      label: 'Entity Statements',
      type: FieldTypeEnum.SWITCH,
      value: row.fund_statement_settings__send_entity_statements,
      editable: true,
      patchEndpoint,
      labelTooltip:
        'Each investor gets a capital account statement per legal entity as well as the consolidated statement.'
    })
  }
  return fields
}

/** The wording fields of one document (a cfg.investor_statement_types code). */
export function getStatementWordingFields(
  settings: FundStatementSettings,
  documentCode: string,
  patchEndpoint: string,
  isConsolidated: boolean
): FieldConfig[] {
  const type = settings.statement_types.find((t) => t.code === documentCode)
  const defaults = type?.text_blocks ?? {}
  const overrides = settings.settings.fund_statement_settings__text_overrides ?? {}

  const mergeFieldHelp = (help?: string | null) => (
    <div>
      {help ? <div className='mb-2'>{help}</div> : null}
      <div className='mb-1'>Merge fields — written in braces, filled in when the statement is made:</div>
      {settings.merge_fields.map((f) => (
        <div key={f.key}>
          <code>{`{${f.key}}`}</code> {f.description}
        </div>
      ))}
    </div>
  )

  return settings.text_blocks
    .filter((block) => block.document === documentCode && (isConsolidated || !block.consolidated_only))
    .map((block): FieldConfig => {
      const fallback = (defaults[block.key] ?? '') || ''
      const own = (overrides[block.key] ?? '') || ''
      const customised = own.trim() !== ''
      return {
        key: `${WORDING_KEY_PREFIX}${block.key}`,
        label: block.label,
        type: FieldTypeEnum.TEXTAREA,
        value: customised ? own : fallback,
        displayValue: customised ? (
          <span>
            {own} <Tag color='purple'>{isConsolidated ? 'This consolidated fund' : 'This fund'}</Tag>
          </span>
        ) : undefined,
        editable: true,
        rows: 2,
        autoSize: { minRows: 2, maxRows: 8 },
        patchEndpoint,
        labelTooltip: mergeFieldHelp(block.help),
        // The blocks share one jsonb column: send the whole map with this
        // block replaced. Text equal to the default is not an override.
        valueTransformer: (value: any, record: any) => {
          const text = typeof value === 'string' ? value : ''
          const current = (record?.[FundStatementSettingsRowFields.fund_statement_settings__text_overrides] ??
            {}) as Record<string, string | null>
          return {
            text_overrides: { ...current, [block.key]: text.trim() === fallback.trim() ? '' : text }
          }
        }
      }
    })
}

export default function StatementSettingsSection({ fundId, fundGroupId, maxLabelWidth }: StatementSettingsSectionProps) {
  const isConsolidated = !!fundGroupId
  const ownerId = fundGroupId || fundId || ''
  const patchEndpoint = isConsolidated ? FUND_GROUP_PATCH : FUND_PATCH
  // On the Statements tab of a consolidated fund the section sits under the
  // "Investor Statements" preview, so it says what it is.
  const title = isConsolidated ? 'Statement Settings' : 'Investor Statements'
  const { settings, loading, error, refetch } = useFundStatementSettings(fundId, fundGroupId)

  const settingFields = useMemo(
    () => (settings ? getStatementSettingFields(settings, patchEndpoint, isConsolidated) : []),
    [settings, patchEndpoint, isConsolidated]
  )
  const wording = useMemo(
    () =>
      settings
        ? settings.statement_types
            .map((type) => ({
              type,
              fields: getStatementWordingFields(settings, type.code, patchEndpoint, isConsolidated)
            }))
            .filter((document) => document.fields.length > 0)
        : [],
    [settings, patchEndpoint, isConsolidated]
  )

  if (!settings) {
    return (
      <Col xs={24}>
        <FormSection title={title} titleColor='purple'>
          {loading ? (
            <div className='flex justify-center py-6'>
              <Spin />
            </div>
          ) : (
            <Alert type='error' showIcon message='Failed to load the statement settings' description={error} />
          )}
        </FormSection>
      </Col>
    )
  }

  const inputManagerProps = {
    inputMode: 'read' as const,
    labelPosition: 'inline' as const,
    enableAutoPatch: true,
    // the PATCH route is keyed by the fund or the consolidated fund
    patchId: ownerId,
    recordId: ownerId,
    currentRecord: settings.settings as Record<string, any>,
    // The saved value, the layout that applies and the default each wording
    // field falls back to are all resolved server-side.
    onPatchSuccess: () => refetch(),
    fieldWrapping: true,
    maxLabelWidth
  }

  return (
    <>
      <Col xs={24} xl={12}>
        <FormSection title={title} titleColor='purple'>
          <InputManager {...inputManagerProps} fields={settingFields} />
        </FormSection>
      </Col>
      {wording.map(({ type, fields }) => (
        <Col xs={24} xl={12} key={type.code}>
          <FormSection title={`${type.label} Wording`} titleColor='purple'>
            <InputManager {...inputManagerProps} fields={fields} />
          </FormSection>
        </Col>
      ))}
    </>
  )
}
